import { useEffect, useMemo, useRef, useState } from 'react'
import { mergeClassNames } from '../../../utils/commonFunctions'
import { CheckBoxSvg, FilterSvg, SearchSvg } from '../svgs/Svgs'
import './TimelineFilter.scss'

export type TimelineFilterType = {
    id: string;
    name: string;
    selected: boolean;
    selectedOptions: string[];
    filterOptions: string[];
}

export type TimelineFilterStateType = {
    active: boolean;
    placement: 'top' | 'bottom' | 'left' | 'right';
    filter: TimelineFilterType[]
}

interface TimelineFilterProps {
    active: boolean;
    disabled?: boolean;
    className?: string
    placement?: 'top' | 'bottom' | 'left' | 'right'
    filter: TimelineFilterType[]
    onActiveChange: () => void;
    setSelectedFilter: (value: string) => void;
    setSelectedFilterOption: (filterId: string, value: string) => void;
}

const TimelineFilter = ({ active, onActiveChange, className, placement = 'top', disabled = false, filter, setSelectedFilter, setSelectedFilterOption }: TimelineFilterProps) => {

    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedFilter = filter.find(item => item.selected);
    const filterApplied = filter.flatMap(item => item.selectedOptions).length;

    const allOptionsSelected = useMemo(() => selectedFilter?.selectedOptions.length === selectedFilter?.filterOptions.length, [selectedFilter]);
    const noOptionsSelected = useMemo(() => selectedFilter?.selectedOptions.length === 0, [selectedFilter]);

    const allChecked = useMemo(() => noOptionsSelected ? 'unchecked' : allOptionsSelected ? 'checked' : 'indeterminate', [selectedFilter])

    useEffect(() => {
        if (!active) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                onActiveChange();
            }
        };

        document.addEventListener('click', handleClickOutside, true);
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [active, onActiveChange]);

    return (
        <div ref={containerRef} className={mergeClassNames('timeline__filter', active && '__active', className)}>
            <button className='__trigger'
                disabled={disabled}
                onClick={() => onActiveChange()}
            >
                <div className='__trigger__icon'>
                    <FilterSvg />
                </div>
                {(filterApplied > 0 && !disabled) &&
                    <div className='__trigger__details'>
                        <div className='__badge'>{filterApplied}</div>
                        <span>Filters</span>
                    </div>
                }
            </button>
            <div className={mergeClassNames('__dialouge', placement)} onClick={(e) => e.stopPropagation()}>
                <div className='__header'>
                    <FilterSvg />
                    <span>Filters</span>
                </div>
                <div className='__body'>
                    <div className='__section'>
                        <span className='__title'>Parent</span>
                        <div className='__card__container'>
                            {filter.map(parent => {
                                const selected = parent.selected ? '__selected' : ''
                                return (
                                    <button key={parent.id} className={mergeClassNames('__task__card', selected)} onClick={() => setSelectedFilter(parent.id)}>
                                        <span className='__task__title' title={parent.name}>{parent.name}</span>
                                        <div className='__badge'>{parent.selectedOptions.length}</div>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                    <div className='__seperator'></div>
                    <div className='__section large'>
                        <div className='__search__container'>
                            <div className='__search'>
                                <SearchSvg />
                                <input type='text' placeholder='Search' value={search} onChange={e => setSearch(e.target.value)} />
                            </div>
                        </div>
                        <div className='__card__container'>
                            <button className={mergeClassNames('__task__card', allOptionsSelected ? '__selected' : '')}
                                onClick={() => setSelectedFilterOption(selectedFilter?.id || '', 'All')}>
                                <CheckBoxSvg checked={allChecked} />
                                <span className='__task__title' title='All'>All</span>
                            </button>
                            {selectedFilter?.filterOptions.filter(option => option.toLowerCase().includes(search.toLowerCase())).map(option => {
                                const selected = selectedFilter.selectedOptions.includes(option) ? '__selected' : ''
                                return (
                                    <button key={option} className={mergeClassNames('__task__card', selected)}
                                        onClick={() => setSelectedFilterOption(selectedFilter.id, option)}>
                                        <CheckBoxSvg checked={selected ? 'checked' : 'unchecked'} />
                                        <span className='__task__title' title={option}>{option}</span>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TimelineFilter